/**
 * Button — the primary action control in all three apps.
 *
 * Used by: every form footer, every toolbar, the POS "Gửi bếp" / "Thanh toán"
 * actions, ConfirmDialog, EmptyState and ErrorState.
 *
 * Two behaviours are built in rather than left to each caller:
 *
 *  1. DOUBLE-SUBMIT PROTECTION. If `onClick` returns a promise the button
 *     disables itself until it settles. On a POS a double-tap on "Thanh toán"
 *     is a second payment; the API dedupes on Idempotency-Key, but the UI must
 *     not rely on that alone. A synchronous handler is guarded for 400ms.
 *  2. A LOADING state that keeps the button's width (the label stays in the
 *     layout, hidden) so a toolbar does not jump while saving.
 *
 * Accessibility: `type="button"` by default (a stray submit inside a form is a
 * classic bug), `aria-busy` while working, minimum 44px touch height at every
 * size except `sm`, which is only for dense desktop tables.
 */
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn, DISABLED, FOCUS_RING } from '../utils.js';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'touch';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 shadow-sm',
  secondary: 'bg-surface text-fg ring-1 ring-inset ring-line-strong hover:bg-surface-alt active:bg-surface-sunken',
  ghost: 'bg-transparent text-fg-muted hover:bg-surface-alt hover:text-fg active:bg-surface-sunken',
  danger: 'bg-danger-solid text-white hover:brightness-95 active:brightness-90 shadow-sm',
  success: 'bg-success-solid text-white hover:brightness-95 active:brightness-90 shadow-sm',
  link: 'bg-transparent text-brand-600 hover:underline underline-offset-4 px-0',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-sm gap-1.5 rounded-sm',
  md: 'min-h-touch px-4 text-sm gap-2 rounded-md',
  lg: 'min-h-touch px-5 text-base gap-2 rounded-md',
  /** POS-grade: thumb-sized, used for the one action that must never be missed. */
  touch: 'min-h-touch-lg px-6 text-lg font-semibold gap-2.5 rounded-lg',
};

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  /** Forces the loading state even when the handler is synchronous. */
  readonly loading?: boolean;
  readonly fullWidth?: boolean;
  readonly leftIcon?: ReactNode;
  readonly rightIcon?: ReactNode;
  /** May return a promise; the button stays disabled until it settles. */
  readonly onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void | Promise<unknown>;
}

/** Guard window for synchronous handlers — long enough to swallow a fat-finger double tap. */
const SYNC_GUARD_MS = 400;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    fullWidth = false,
    leftIcon,
    rightIcon,
    onClick,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (busy || loading || disabled === true) {
        event.preventDefault();
        return;
      }
      const result = onClick?.(event);
      if (result !== undefined && typeof (result as Promise<unknown>).then === 'function') {
        setBusy(true);
        void (result as Promise<unknown>).finally(() => {
          if (mounted.current) setBusy(false);
        });
        return;
      }
      // Synchronous handler: still swallow an immediate second click.
      //
      // EXCEPT for type="submit". Flipping `busy` here re-renders the button
      // as `disabled` synchronously (React 18 flushes discrete-event state
      // before the browser runs the click's DEFAULT ACTION), and a disabled
      // submit button cancels form submission — so the form would NEVER
      // submit, not even once. Submit buttons get their double-submit
      // protection from the form's own submitting state driving `loading`.
      if (type === 'submit') return;
      setBusy(true);
      window.setTimeout(() => {
        if (mounted.current) setBusy(false);
      }, SYNC_GUARD_MS);
    },
    [busy, disabled, loading, onClick],
  );

  const isBusy = busy || loading;

  return (
    <button
      ref={ref}
      type={type}
      onClick={handleClick}
      disabled={disabled === true || isBusy}
      aria-busy={isBusy}
      className={cn(
        'relative inline-flex select-none items-center justify-center font-medium transition-colors',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        FOCUS_RING,
        DISABLED,
        className,
      )}
      {...rest}
    >
      {isBusy ? <Spinner /> : null}
      <span className={cn('inline-flex items-center gap-2', isBusy && 'invisible')}>
        {leftIcon}
        {children}
        {rightIcon}
      </span>
    </button>
  );
});

/** Shared with LoadingState; kept private so there is one spinner shape. */
function Spinner(): React.JSX.Element {
  return (
    <svg
      className="absolute h-5 w-5 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path className="opacity-90" fill="currentColor" d="M12 2a10 10 0 0 1 10 10h-3a7 7 0 0 0-7-7V2Z" />
    </svg>
  );
}
